import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import type { AdminApi } from "../api/client.js";
import type { AuditAction, AuditListQuery } from "../api/types.js";
import { Card } from "../components/Card.js";
import { Field } from "../components/Field.js";
import { Select } from "../components/Select.js";
import { ErrorBanner } from "../components/ErrorBanner.js";
import { formatDateTime } from "../lib/format.js";

type Props = { api: AdminApi };

const ACTION_OPTIONS: { value: "" | AuditAction; label: string }[] = [
  { value: "", label: "All actions" },
  { value: "list_appointments", label: "List appointments" },
  { value: "view_appointment", label: "View appointment" },
  { value: "view_document", label: "View document" },
  { value: "cancel_appointment", label: "Cancel appointment" },
  { value: "update_calendar_config", label: "Update calendar config" },
  { value: "google_calendar_connected", label: "Google Calendar connected" },
  {
    value: "google_calendar_disconnected",
    label: "Google Calendar disconnected",
  },
  { value: "retention_run", label: "Retention run" },
];

export function AuditLogPage({ api }: Props) {
  const [action, setAction] = useState<"" | AuditAction>("");
  const [actor, setActor] = useState("");

  const query = useInfiniteQuery({
    queryKey: ["admin", "audit", action, actor],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      const q: AuditListQuery = { limit: 50 };
      if (pageParam) q.before = pageParam;
      if (action) q.action = action;
      const trimmed = actor.trim();
      if (trimmed) q.actor = trimmed;
      return api.listAuditLog(q);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const entries = query.data?.pages.flatMap((p) => p.entries) ?? [];

  return (
    <div className="grid gap-4">
      <Card title="Filters">
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Action"
            options={ACTION_OPTIONS}
            value={action}
            onChange={(e) => setAction(e.target.value as "" | AuditAction)}
          />
          <Field
            label="Actor email"
            type="search"
            placeholder="admin@example.com"
            value={actor}
            onChange={(e) => setActor(e.target.value)}
          />
        </div>
      </Card>

      <Card title={`Entries (${entries.length})`}>
        {query.isPending ? (
          <p className="text-sm text-slate-600">Loading…</p>
        ) : null}
        {query.isError ? (
          <ErrorBanner message="Could not load audit log." />
        ) : null}
        {!query.isPending && entries.length === 0 ? (
          <p className="text-sm text-slate-600">No entries match.</p>
        ) : null}
        {entries.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead>
                <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">Actor</th>
                  <th className="py-2 pr-3">Action</th>
                  <th className="py-2 pr-3">Appointment</th>
                  <th className="py-2 pr-3">Document</th>
                  <th className="py-2 pr-3">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm text-slate-800">
                {entries.map((e) => (
                  <tr key={e.id}>
                    <td className="py-2 pr-3 whitespace-nowrap">
                      {formatDateTime(e.occurred_at)}
                    </td>
                    <td className="py-2 pr-3">{e.actor_email}</td>
                    <td className="py-2 pr-3 font-mono text-xs">
                      {e.action}
                    </td>
                    <td className="py-2 pr-3">
                      {e.appointment_id ? (
                        <Link
                          to={`/appointments/${e.appointment_id}`}
                          className="font-mono text-xs text-blue-700 hover:underline"
                        >
                          {e.appointment_id.slice(0, 8)}…
                        </Link>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs text-slate-600">
                      {e.document_id ? `${e.document_id.slice(0, 8)}…` : "—"}
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs text-slate-600">
                      {e.ip_address ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {query.hasNextPage ? (
          <div className="mt-4 flex justify-center">
            <button
              type="button"
              onClick={() => {
                void query.fetchNextPage();
              }}
              disabled={query.isFetchingNextPage}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {query.isFetchingNextPage ? "Loading…" : "Load more"}
            </button>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
